import axios, { AxiosRequestConfig } from 'axios';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';
import IResponse from '../interfaces/IResponse';

const BASE_URL = `${baseURL}api/factory-delivery-notices`;

export interface IDeliveryNoticeFactory {
    _id: string;
    factoryName: string;
    image?: string;
    notices: IDeliveryNotice[];
    createdAt?: string;
    updatedAt?: string;
}

export interface IDeliveryNotice {
    _id: string;
    deliveryDates: string[];
    message?: string;
    isCompleted?: boolean;
    completedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface IDeliveryNotices {
    _id: string;
    factories: IDeliveryNoticeFactory[];
    createdAt?: string;
    updatedAt?: string;
}

export interface IDeliveryNoticeFactoryPayload {
    factoryName: string;
    image?: File | null;
}

export interface IDeliveryNoticePayload {
    deliveryDates: string[];
    message?: string;
}

interface IDeliveryNoticesResponse extends IResponse {
    deliveryNotices: IDeliveryNotices | null;
}

const getMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error) && error.response) {
        return {
            message: error.response.data?.message || fallback,
            status: error.response.status || 400,
        };
    }

    return { message: fallback, status: 400 };
};

const toFormData = (payload: IDeliveryNoticeFactoryPayload) => {
    const formData = new FormData();

    formData.append('factoryName', payload.factoryName);
    if (payload.image) formData.append('image', payload.image);

    return formData;
};

const getDeliveryNotices = async (): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.get(BASE_URL, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Pobrano awizacje dostaw',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie pobrano awizacji dostaw');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const addFactory = async (payload: IDeliveryNoticeFactoryPayload): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeaderForFiles(),
        };

        const res = await axios.post(`${BASE_URL}/factories`, toFormData(payload), config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Dodano fabryke',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie dodano fabryki');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const editFactory = async (_id: string, payload: IDeliveryNoticeFactoryPayload): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeaderForFiles(),
        };

        const res = await axios.put(`${BASE_URL}/factories/${_id}`, toFormData(payload), config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Zapisano fabryke',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie zapisano fabryki');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const addNotice = async (_id: string, payload: IDeliveryNoticePayload): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.post(`${BASE_URL}/factories/${_id}/notices`, payload, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Dodano awizacje',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie dodano awizacji');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const editNotice = async (_id: string, noticeId: string, payload: IDeliveryNoticePayload): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.put(`${BASE_URL}/factories/${_id}/notices/${noticeId}`, payload, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Zapisano awizacje',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie zapisano awizacji');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const deleteNotice = async (_id: string, noticeId: string): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.delete(`${BASE_URL}/factories/${_id}/notices/${noticeId}`, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Usunieto awizacje',
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie usunieto awizacji');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const completeNotice = async (_id: string, noticeId: string, completed = true): Promise<IDeliveryNoticesResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.put(`${BASE_URL}/factories/${_id}/notices/${noticeId}/complete`, { completed }, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || (completed ? 'Oznaczono dostawę jako dostarczoną' : 'Cofnięto oznaczenie dostawy'),
            deliveryNotices: res.data?.deliveryNotices || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, completed ? 'Nie oznaczono dostawy jako dostarczonej' : 'Nie cofnięto oznaczenia dostawy');

        return {
            ok: false,
            status,
            message,
            deliveryNotices: null,
        };
    }
};

const deleteFactory = async (_id: string): Promise<IResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.delete(`${BASE_URL}/factories/${_id}`, config);

        return {
            ok: true,
            status: res.status,
            message: 'Usunieto fabryke',
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie usunieto fabryki');

        return {
            ok: false,
            status,
            message,
        };
    }
};

const factoryDeliveryNoticesController = {
    getDeliveryNotices,
    addFactory,
    editFactory,
    addNotice,
    editNotice,
    deleteNotice,
    completeNotice,
    deleteFactory,
};

export default factoryDeliveryNoticesController;
